import { Tooltip as ReactTooltip } from "react-tooltip"
import { CellContext } from "@tanstack/react-table"
import styled from "styled-components"

import { UITypes } from "types"

import type { KeyIndicatorsTooltipBlock } from "pages/contractor/IntelligenceReportsPage/convertIntelligenceReportsToTableRows"

const StyledTooltip = styled(ReactTooltip)`
  background: #000 !important;
  color: #fff !important;
  font-size: 14px !important;
  padding: 5px !important;
  border-radius: 6px !important;
  max-width: 402px !important;
  z-index: 1100 !important;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 40px;
  overflow: hidden;
  cursor: help;
`

const Line = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 14px;
  font-weight: 400;
  color: var(--text-primary);
  line-height: 20px;
  min-height: 0;
  overflow: hidden;

  &::before {
    content: "•";
    flex-shrink: 0;
    color: var(--text-muted);
  }
`

const LineContent = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TooltipContent = styled.div`
  background: #000;
  color: #fff;
  font-size: 14px;
  padding: 5px;
  border-radius: 6px;
  min-width: 200px;
  max-width: 402px;
`

const TooltipBlock = styled.div`
  padding-bottom: 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid #1e293b;

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 12px;
  }
`

const BlockTitle = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`

const BlockItem = styled.div`
  font-weight: 400;
  padding-left: 12px;
  position: relative;
  margin-bottom: 2px;

  &::before {
    content: "•";
    position: absolute;
    left: 0;
    color: #fff;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

interface Props {
  cell: CellContext<UITypes.TableBodyRow, UITypes.TableBodyCell>
}

const BULLET_SEPARATORS = /[,;]+\s*|\n+/

export const IntelligenceReportsKeyIndicatorCell = ({ cell }: Props) => {
  const rawValue = cell.getValue()?.value
  const valueStr = rawValue != null ? String(rawValue).trim() : ""
  const rowItem = cell.row.original.rowSettings?.item as
    | { key_indicators_tooltip?: KeyIndicatorsTooltipBlock[] }
    | undefined
  const tooltipBlocks = rowItem?.key_indicators_tooltip ?? []

  const items = valueStr
    ? valueStr
        .split(BULLET_SEPARATORS)
        .map(s => s.trim())
        .filter(Boolean)
    : []

  const lines = items.slice(0, 2)
  const rowId = cell.row.original.rowSettings?.id ?? cell.row.id
  const tooltipId = `key-indicators-tooltip-${rowId}`

  if (lines.length === 0) {
    return <span>-</span>
  }

  return (
    <>
      <Container data-tooltip-id={tooltipId}>
        {lines.map((text, idx) => (
          <Line key={idx}>
            <LineContent>{text}</LineContent>
          </Line>
        ))}
      </Container>
      <StyledTooltip id={tooltipId} place="bottom-start">
        {tooltipBlocks.length > 0 ? (
          <TooltipContent>
            {tooltipBlocks.map((block, idx) => (
              <TooltipBlock key={idx}>
                <BlockTitle>{block.title}</BlockTitle>
                {block.items.map((item, itemIdx) => (
                  <BlockItem key={itemIdx}>{item}</BlockItem>
                ))}
              </TooltipBlock>
            ))}
          </TooltipContent>
        ) : null}
      </StyledTooltip>
    </>
  )
}
